import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { userApi } from '../../services/userApi';
import { authApi } from '../../services/authApi';
import { useAuthStore } from '../../stores/authStore';

/**
 * Trigger a JSON download of the export payload. We build a Blob and
 * use a programmatic anchor click rather than `window.location.href` so
 * it works in all environments (and is testable via Jest's URL/Blob
 * mocks).
 */
function triggerDownload(filename: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Account page — data export (§28.3) and account deletion (§10.8).
 *
 * Deletion uses a typed-confirmation pattern: the user must type their
 * email address before the Delete button is enabled. This is a strong
 * "destructive action confirmation" pattern that prevents accidental
 * deletion (WCAG 3.3.4) without obscuring the path for power users.
 */
export function AccountPage() {
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const claims = useAuthStore((s) => s.claims);
  const userEmail = claims?.email ?? '';

  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function onExport() {
    setExportError(null);
    setExporting(true);
    try {
      const payload = await userApi.exportAll();
      triggerDownload(`roundrecon-export-${Date.now()}.json`, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed.';
      setExportError(message);
    } finally {
      setExporting(false);
    }
  }

  async function onConfirmDelete() {
    setDeleteError(null);
    setDeleting(true);
    try {
      await userApi.deleteAccount();
      // Best-effort logout; ignore failures since the account is gone.
      try {
        await authApi.logout();
      } catch {
        /* ignore */
      }
      signOut();
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Account deletion failed.';
      setDeleteError(message);
      setDeleting(false);
    }
  }

  const emailMatches =
    userEmail.length > 0 && confirmEmail.trim().toLowerCase() === userEmail.toLowerCase();

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Account
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h2" component="h2" sx={{ fontSize: '1.1rem', mb: 1 }}>
          Export your data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download a JSON file with your account, alerts, saved searches, watchlist, and consent
          settings.
        </Typography>
        {exportError ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        ) : null}
        <Button variant="outlined" onClick={onExport} disabled={exporting} aria-busy={exporting}>
          {exporting ? 'Preparing export…' : 'Export my data'}
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderColor: 'error.main' }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.1rem', mb: 1, color: 'error.main' }}
        >
          Delete your account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This will permanently delete your account and every alert, saved search, and watched
          listing tied to it. This cannot be undone.
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setConfirmOpen(true);
            setConfirmEmail('');
            setDeleteError(null);
          }}
        >
          Delete my account
        </Button>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => (deleting ? null : setConfirmOpen(false))}
        aria-labelledby="confirm-delete-account-title"
        aria-describedby="confirm-delete-account-desc"
      >
        <DialogTitle id="confirm-delete-account-title">Delete account?</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-account-desc">
            This will permanently delete your account and all alerts, saved searches, and watched
            listings. This cannot be undone. Type your email ({userEmail || 'your email'}) below to
            confirm.
          </DialogContentText>
          {deleteError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Confirm by typing your email"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              fullWidth
              autoComplete="off"
              inputProps={{ 'aria-label': 'Type your email to confirm account deletion' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmDelete}
            disabled={!emailMatches || deleting}
            aria-busy={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete my account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AccountPage;
